/**
 * PolicyViolationAlert Component
 * 
 * Displays policy violations with appropriate styling and severity indicators.
 * Automatically consolidates multiple violations of the same type to reduce redundancy.
 */

import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import type { PolicyViolation } from '@/data/mockPolicyViolations';
import type { PolicyCheckType } from '@/types/timesheetSettings';
import { consolidatePolicyViolations, shouldConsolidate, VIOLATION_TYPE_PRIORITY } from '@/services/policyViolationConsolidationService';

interface PolicyViolationAlertProps {
  violations: PolicyViolation[];
  compact?: boolean;
}

const getViolationIcon = (severity: 'low' | 'medium' | 'high') => {
  switch (severity) {
    case 'high':
      return <AlertCircle className="h-4 w-4" />;
    case 'medium':
      return <AlertTriangle className="h-4 w-4" />;
    case 'low':
      return <Info className="h-4 w-4" />;
  }
};

const getViolationVariant = (severity: 'low' | 'medium' | 'high') => {
  switch (severity) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    case 'low':
      return 'default';
  }
};

const getViolationTypeLabel = (type: PolicyCheckType): string => {
  switch (type) {
    case 'daily-overage':
      return 'Daily Overage';
    case 'weekly-overage':
      return 'Weekly Overage';
    case 'legal-daily-maximum':
      return 'Legal Maximum (Daily)';
    case 'legal-weekly-maximum':
      return 'Legal Maximum (Weekly)';
    case 'missing-hours':
      return 'Missing Hours';
    case 'non-working-day':
      return 'Non-Working Day';
    default:
      return type;
  }
};

const getSeverityBadgeVariant = (severity: 'low' | 'medium' | 'high') => {
  switch (severity) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    case 'low':
      return 'secondary';
  }
};

export function PolicyViolationAlert({ violations, compact = false }: PolicyViolationAlertProps) {
  if (violations.length === 0) {
    return null;
  }

  // Group violations by severity
  const errorViolations = violations.filter(v => v.severity === 'high');
  const warningViolations = violations.filter(v => v.severity === 'medium');
  const infoViolations = violations.filter(v => v.severity === 'low');

  if (compact) {
    // Compact view - just show counts
    return (
      <div className="flex items-center gap-2">
        {errorViolations.length > 0 && (
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            {errorViolations.length}
          </Badge>
        )}
        {warningViolations.length > 0 && (
          <Badge variant="default" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            {warningViolations.length}
          </Badge>
        )}
        {infoViolations.length > 0 && (
          <Badge variant="secondary" className="gap-1">
            <Info className="h-3 w-3" />
            {infoViolations.length}
          </Badge>
        )}
      </div>
    );
  }

  // Check if consolidation should be applied
  const useConsolidation = shouldConsolidate(violations);
  
  if (useConsolidation) {
    // Consolidate violations by type
    const consolidated = consolidatePolicyViolations(violations);
    
    return (
      <div className="space-y-3">
        {consolidated.map((group) => (
          <Alert
            key={group.id}
            variant={getViolationVariant(group.severity)}
            className="relative"
            showIcon={false}
          >
            <div className="flex items-start gap-3">
              {getViolationIcon(group.severity)}
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTitle className="text-sm font-medium">
                    {group.summary}
                  </AlertTitle>
                  <Badge variant={getSeverityBadgeVariant(group.severity)} className="text-xs">
                    {group.severity.toUpperCase()}
                  </Badge>
                </div>
                
                {/* List of violations */}
                <div className="space-y-1">
                  {group.details.map((detail, index) => (
                    <div key={index} className="text-sm">
                      {detail.day && <span className="font-medium">{detail.day}: </span>}
                      {detail.message}
                    </div>
                  ))}
                </div>
                
                {/* Insight message */}
                <AlertDescription className="text-sm mt-2 pt-2 border-t">
                  {group.insight}
                </AlertDescription>
              </div>
            </div>
          </Alert>
        ))}
      </div>
    );
  }

  // Sort violations by severity then type priority
  const sortedViolations = [...violations].sort((a, b) => {
    const severityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const severityDiff = (severityOrder[a.severity] ?? 2) - (severityOrder[b.severity] ?? 2);
    if (severityDiff !== 0) return severityDiff;
    const priorityA = VIOLATION_TYPE_PRIORITY[a.violationType] ?? 99;
    const priorityB = VIOLATION_TYPE_PRIORITY[b.violationType] ?? 99;
    return priorityA - priorityB;
  });

  // Full view - show individual violations (when no consolidation needed)
  return (
    <div className="space-y-2">
      {sortedViolations.map((violation) => (
        <Alert
          key={violation.id}
          variant={getViolationVariant(violation.severity)}
          className="relative"
          showIcon={false}
        >
          <div className="flex items-start gap-3">
            {getViolationIcon(violation.severity)}
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <AlertTitle className="text-sm font-medium">
                  {getViolationTypeLabel(violation.violationType)}
                </AlertTitle>
                <Badge variant={getSeverityBadgeVariant(violation.severity)} className="text-xs">
                  {violation.severity.toUpperCase()}
                </Badge>
              </div>
              <AlertDescription className="text-sm">
                {violation.message}
                {violation.date && (
                  <span className="ml-2 text-muted-foreground">
                    ({new Date(violation.date).toLocaleDateString()})
                  </span>
                )}
              </AlertDescription>
              <div className="text-xs text-muted-foreground mt-1">
                {violation.violationType === 'missing-hours' ? (
                  <>
                    Expected: {violation.details.expected}h • 
                    Actual: {violation.details.actual}h • 
                    Minimum: {violation.details.threshold}h
                  </>
                ) : violation.violationType.includes('legal-') ? (
                  <>
                    Expected: {violation.details.expected}h • 
                    Actual: {violation.details.actual}h • 
                    Legal Limit: {violation.details.limit}h
                  </>
                ) : (
                  <>
                    Expected: {violation.details.expected}h • 
                    Actual: {violation.details.actual}h • 
                    Threshold: {violation.details.threshold}h
                  </>
                )}
              </div>
            </div>
          </div>
        </Alert>
      ))}
    </div>
  );
}

/**
 * PolicyViolationSummary Component
 * 
 * Shows a summary of violations grouped by type
 */
interface PolicyViolationSummaryProps {
  violations: PolicyViolation[];
}

export function PolicyViolationSummary({ violations }: PolicyViolationSummaryProps) {
  if (violations.length === 0) {
    return (
      <Alert showIcon={false}>
        <Info className="h-4 w-4" />
        <AlertTitle>No Policy Violations</AlertTitle>
        <AlertDescription>
          All timesheets are within expected hours and policy limits.
        </AlertDescription>
      </Alert>
    );
  }

  const summary = {
    total: violations.length,
    byType: {
      dailyOverage: violations.filter(v => v.violationType === 'daily-overage').length,
      weeklyOverage: violations.filter(v => v.violationType === 'weekly-overage').length,
      legalMaximumDaily: violations.filter(v => v.violationType === 'legal-daily-maximum').length,
      legalMaximumWeekly: violations.filter(v => v.violationType === 'legal-weekly-maximum').length,
      missingHours: violations.filter(v => v.violationType === 'missing-hours').length,
    },
    bySeverity: {
      high: violations.filter(v => v.severity === 'high').length,
      medium: violations.filter(v => v.severity === 'medium').length,
      low: violations.filter(v => v.severity === 'low').length,
    },
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {summary.bySeverity.high > 0 && (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <div>
                <div className="text-2xl font-bold">{summary.bySeverity.high}</div>
                <div className="text-sm">High</div>
              </div>
            </div>
          </div>
        )}
        {summary.bySeverity.medium > 0 && (
          <div className="rounded-lg border border-orange-500 bg-orange-50 p-4">
            <div className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="h-5 w-5" />
              <div>
                <div className="text-2xl font-bold">{summary.bySeverity.medium}</div>
                <div className="text-sm">Medium</div>
              </div>
            </div>
          </div>
        )}
        {summary.bySeverity.low > 0 && (
          <div className="rounded-lg border border-blue-500 bg-blue-50 p-4">
            <div className="flex items-center gap-2 text-blue-700">
              <Info className="h-5 w-5" />
              <div>
                <div className="text-2xl font-bold">{summary.bySeverity.low}</div>
                <div className="text-sm">Low</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-medium">Violations by Type</h4>
        <div className="space-y-1 text-sm">
          {summary.byType.dailyOverage > 0 && (
            <div className="flex justify-between">
              <span>Daily Overage</span>
              <Badge variant="secondary">{summary.byType.dailyOverage}</Badge>
            </div>
          )}
          {summary.byType.weeklyOverage > 0 && (
            <div className="flex justify-between">
              <span>Weekly Overage</span>
              <Badge variant="secondary">{summary.byType.weeklyOverage}</Badge>
            </div>
          )}
          {summary.byType.legalMaximumDaily > 0 && (
            <div className="flex justify-between">
              <span>Legal Maximum (Daily)</span>
              <Badge variant="destructive">{summary.byType.legalMaximumDaily}</Badge>
            </div>
          )}
          {summary.byType.legalMaximumWeekly > 0 && (
            <div className="flex justify-between">
              <span>Legal Maximum (Weekly)</span>
              <Badge variant="destructive">{summary.byType.legalMaximumWeekly}</Badge>
            </div>
          )}
          {summary.byType.missingHours > 0 && (
            <div className="flex justify-between">
              <span>Missing Hours</span>
              <Badge variant="secondary">{summary.byType.missingHours}</Badge>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
